import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import {
  getThreads,
  createThread,
  deleteThread,
} from '../services/api';
import './Forum.css';

interface Thread {
  id: number;
  user_id: number;
  username: string;
  title: string;
  body: string;
  created_at: string;
  reply_count: number;
}

const Forum: React.FC = () => {
  const { user, isAdmin } = useAuth();
  const [threads, setThreads] = useState<Thread[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await getThreads();
      setThreads(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!title.trim() || !body.trim()) {
      setError('Add a title and a message.');
      return;
    }
    setSubmitting(true);
    try {
      await createThread({ title: title.trim(), body: body.trim() });
      setTitle('');
      setBody('');
      setShowForm(false);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Could not post. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id: number) => {
    if (!window.confirm('Delete this thread?')) return;
    try {
      await deleteThread(id);
      await load();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="forum-page">
      <section className="forum-hero">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            style={{ textAlign: 'center' }}
          >
            <span className="page-badge">Community Forum</span>
            <h1 className="forum-title">
              Talk to<br />
              <span className="gradient-text">Kutloano</span>
            </h1>
            <p className="forum-subtitle">
              Share your thoughts, ask questions, and connect. Kutloano reads and replies.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="forum-content section">
        <div className="container">
          <div className="forum-actions">
            <div className="forum-count">
              {threads.length} {threads.length === 1 ? 'conversation' : 'conversations'}
            </div>
            {user ? (
              <button className="btn btn-primary" onClick={() => setShowForm(true)}>
                + Start a Conversation
              </button>
            ) : (
              <Link to="/login" className="btn btn-primary">
                Sign in to post
              </Link>
            )}
          </div>

          {loading ? (
            <div className="loading-container">
              <div className="spinner" />
            </div>
          ) : threads.length === 0 ? (
            <div className="empty-state">
              <p>No conversations yet. Be the first to start one.</p>
            </div>
          ) : (
            <div className="thread-list">
              {threads.map((t, i) => (
                <motion.div
                  key={t.id}
                  className="thread-card"
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                >
                  <Link to={`/forum/${t.id}`} className="thread-link">
                    <div className="thread-head">
                      <h3>{t.title}</h3>
                      <span className="thread-replies">
                        {t.reply_count} {t.reply_count === 1 ? 'reply' : 'replies'}
                      </span>
                    </div>
                    <p className="thread-preview">{t.body}</p>
                    <div className="thread-meta">
                      <span className="thread-author">{t.username}</span>
                      <span className="thread-dot">&bull;</span>
                      <span>{new Date(t.created_at).toLocaleDateString()}</span>
                    </div>
                  </Link>
                  {(isAdmin || user?.id === t.user_id) && (
                    <button
                      className="thread-delete"
                      onClick={() => remove(t.id)}
                      aria-label="Delete thread"
                    >
                      &times;
                    </button>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      <AnimatePresence>
        {showForm && (
          <motion.div
            className="forum-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowForm(false)}
          >
            <motion.div
              className="forum-modal"
              initial={{ opacity: 0, y: 40, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.96 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="forum-modal-header">
                <h2>Start a Conversation</h2>
                <button className="forum-modal-close" onClick={() => setShowForm(false)}>
                  &times;
                </button>
              </div>
              <form onSubmit={submit}>
                <div className="form-group">
                  <label className="form-label">Topic</label>
                  <input
                    className="form-input"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="What's on your mind?"
                    maxLength={140}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Message</label>
                  <textarea
                    className="form-input form-textarea"
                    style={{ minHeight: 180 }}
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder="Share your thoughts, a question, or reflections on a piece..."
                  />
                </div>
                {error && <div className="form-error">{error}</div>}
                <button type="submit" className="btn btn-primary" disabled={submitting} style={{ width: '100%', justifyContent: 'center' }}>
                  {submitting ? 'Posting...' : 'Post'}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Forum;
